import { motion } from "framer-motion";
import { Layers, Grid3X3, Mountain, TestTube2, Wrench, ArrowRight } from "lucide-react";

const services = [
  {
    icon: Layers,
    title: "Asphalt Coring",
    desc: "Clean, precise pavement cores for thickness verification, mix design, and quality assurance testing on roadways, parking lots, and drive lanes.",
    tags: ["Pavement QA/QC", "Thickness Verification", "TxDOT Projects"],
    span: "lg:col-span-7",
  },
  {
    icon: Grid3X3,
    title: "Concrete Coring",
    desc: "Diamond coring of slabs, walls, and structures for strength testing, utility penetrations, and structural evaluation.",
    tags: ["Strength Testing", "Utility Penetrations"],
    span: "lg:col-span-5",
  },
  {
    icon: Mountain,
    title: "Geotechnical & Environmental Boring",
    desc: "Soil borings for foundation design, environmental assessment, and subsurface characterization. Auger and wet-rotary methods available.",
    tags: ["Foundation Design", "Environmental", "Monitoring Wells"],
    span: "lg:col-span-5",
  },
  {
    icon: TestTube2,
    title: "Soil Sampling & Subsurface Investigation",
    desc: "Undisturbed and disturbed sampling, SPT testing, and detailed boring logs delivered with professional documentation.",
    tags: ["SPT / Shelby Tube", "Boring Logs", "Lab Coordination"],
    span: "lg:col-span-7",
  },
  {
    icon: Wrench,
    title: "Utility & Pavement Investigation Support",
    desc: "Project support for engineering firms and contractors: utility locates, pavement evaluation programs, and custom scope field services.",
    tags: ["Utility Locates", "Pavement Evaluation", "Custom Scopes"],
    span: "lg:col-span-12",
  },
];

export default function Services() {
  return (
    <section id="services" className="py-24 lg:py-32 bg-[#F4F5F7]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-4 font-mono-tech" data-testid="services-overline">
            Services • 03
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#0F172A]" data-testid="services-heading">
            Coring & Boring Services in Houston
          </h2>
          <p className="mt-6 text-base text-slate-600 leading-relaxed" data-testid="services-subcopy">
            From a single asphalt core to full geotechnical drilling programs, Aegis Subsurface delivers the field data
            your project depends on.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="services-grid">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: i * 0.08, ease: "easeOut" }}
              className={`${s.span} group bg-white border border-slate-200 rounded-sm p-8 transition-all duration-200 hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0F172A] hover:border-[#0F172A]`}
              data-testid={`service-card-${i}`}
            >
              <div className="flex items-start justify-between">
                <s.icon className="w-10 h-10 text-[#FF5A00]" />
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#FF5A00] group-hover:translate-x-1 transition-all duration-200" />
              </div>
              <h3 className="mt-6 font-heading text-xl sm:text-2xl font-bold tracking-tight text-[#0F172A] uppercase">
                {s.title}
              </h3>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{s.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {s.tags.map((t) => (
                  <span
                    key={t}
                    className="text-xs font-semibold uppercase tracking-wider text-[#0F172A] bg-[#F4F5F7] border border-slate-200 px-3 py-1.5 rounded-sm"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 bg-[#0F172A] border-l-4 border-[#FF5A00] px-8 py-6 rounded-sm" data-testid="services-pricing-note">
          <p className="text-base text-slate-200 leading-relaxed">
            <span className="font-heading font-bold uppercase text-white">Pricing note:</span> boring and coring prices
            are quoted per project based on scope, depth, soil conditions, site access, and location.{" "}
            <a href="#quote" className="text-[#FF5A00] font-semibold underline underline-offset-4 hover:text-[#E04F00]" data-testid="services-pricing-link">
              Request a quote
            </a>{" "}
            for exact pricing on your project.
          </p>
        </div>
      </div>
    </section>
  );
}
